import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  nothing,
  classMap,
  styleMap,
} from 'omni-ui';
import { produce } from 'immer';
import './campaign-attn-calc-multi-rec-dropdowns.js';
import './campaign-attn-calc-multi-rec-score.js';

OmniStyleElement.register();

/**
 * Class for Attention Calculator Multi Rec Module Element
 * @extends OmniElement
 */
export class CampaignAttnCalcMultiRec extends OmniElement {
  /**
   * Attention Calculator Multi Rec Lit Properties
   * @property {Object} context - Values passed in from wrapper/router
   * @property {Object} savedData - Data already saved in the API
   * @property {Object} schema - Values passed in from wrapper/router
   * @property {Number} currentAttentionStep - current step displayed in the wizard
   * @property {Object} attentionData - data for dropdown questions that is saved to savedData
   * @property {Array} completedAttentionSteps - array of completed steps with each element representing the step number
   * @property {Object} scores - unfiltered outputs and scores passed
   * @property {Number} weightTotal - Sum of weights of all questions used to calculate scores
   * @property {Number} recommendationCount - Number of recommendations to be displayed
   */
  static get properties() {
    return {
      context: { type: Object },
      savedData: { type: Object },
      schema: { type: Object },
      currentAttentionStep: { type: Number },
      attentionData: { type: Object },
      completedAttentionSteps: { type: Array },
      scores: { type: Object },
      weightTotal: { type: Number },
      recommendationCount: { type: Number },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .title-wrapper {
          margin-left: 2.286rem;
        }
        omni-tile.module-wrapper::part(body-slot) {
          padding: 2.413rem 2.857rem 1.429rem 0;
        }
        .wizard-container {
          width: 50%;
          display: flex;
          justify-content: stretch;
          align-items: flex-end;
          margin: auto;
        }
        .wizard-step {
          flex-shrink: 0;
          flex-grow: 0;
          position: relative;
          width: 2.5rem;
          height: 2.5rem;
        }
        .wizard-line {
          height: 2px;
          margin-bottom: calc(1.25rem - 1px);
          flex-grow: 1;
          background-color: rgb(var(--rgb-nav-one));
        }
        .wizard-line-complete {
          background-color: rgb(var(--rgb-primary));
        }
        .omni button.wizard-button {
          border-radius: 50%;
          height: 2.5rem;
          width: 2.5rem;
          padding: 0px 5px;
          background-color: transparent;
          border: 2px solid rgb(var(--rgb-nav-one));
        }
        .omni button.wizard-button-complete {
          border-color: rgb(var(--rgb-secondary));
          background-image: var(--gradient-primary);
          color: rgb(var(--rgb-core-light));
        }
        .omni button.wizard-button-active,
        .omni button.wizard-button:focus,
        .omni button.wizard-button:hover {
          border: 2px solid rgb(var(--rgb-primary));
          color: rgb(var(--rgb-primary));
          background-color: transparent;
        }
        .omni button.wizard-button-active + .wizard-step-title,
        .omni button.wizard-button:focus + .wizard-step-title,
        .omni button.wizard-button:hover + .wizard-step-title {
          color: rgb(var(--rgb-primary));
          transition:
            background-color 0.25s ease 0s,
            border-color 0.25s ease 0s,
            color 0.25s ease 0s;
        }
        .wizard-step-title {
          position: absolute;
          text-align: center;
          bottom: 2.5rem;
          padding-bottom: 0.357rem;
          left: -150%;
          width: 400%;
          transition:
            background-color 0.25s ease 0s,
            border-color 0.25s ease 0s,
            color 0.25s ease 0s;
        }
        @media screen and (max-width: 1260px) {
          .wizard-container {
            width: 70%;
          }
        }
      `,
    ];
  }

  constructor() {
    super();
    this.isInitializing = true;
    this.currentAttentionStep = 0;
    this.completedAttentionSteps = [];
  }

  /**
   * Sets up event listener for the 'recommendation-update' event when the component is connected.
   * @listens event:recommendation-update
   */
  connectedCallback() {
    super.connectedCallback();
    this.attentionData = { ...(this.savedData?.attentionData || {}) };
    this.completedAttentionSteps = [
      ...(this.savedData?.completedAttentionSteps || []),
    ];
    this.recommendationCount = this.savedData?.recommendationCount ?? 1;

    this.addEventListener(
      'recommendation-update',
      this._dispatchModuleChangedEvent,
    );
  }

  /**
   * Removes event listener for the 'recommendation-update' event when the component is disconnected.
   */
  disconnectedCallback() {
    this.removeEventListener(
      'recommendation-update',
      this._dispatchModuleChangedEvent,
    );
    super.disconnectedCallback();
  }

  async firstUpdated() {
    this._calculateScores();
    await this.updateComplete;
    this.isInitializing = false;
  }

  /**
   * @description Handles any input field changes by firing {@link #_dispatchModuleChangedEvent} to notify step detail that {@link #attentionData} has been changed
   * Calls {@link #_calculateScores}
   * Checks to determine whether all questions on an attention calculator "step" have been answered, and updates {@link #completedAttentionSteps} accordingly
   * @param {object} value - The value of the input used in {@link _renderDropdown}; represents the option in the schema for the given question label and value
   * @param {object} questionLabel - The question label used as a key to determine the value of the dropdown in {@link #attentionData}
   * @param {object} stepLabel - The step label used as a key to determine the value of the dropdown in {@link #attentionData}
   * @param {string} dropdownType - Flag to denote if dropdown is question or weight
   */
  _handleChange = async ({
    detail: { value, questionLabel, stepLabel, dropdownType },
  }) => {
    this.attentionData = produce(this.attentionData, draft => {
      draft[stepLabel] = draft[stepLabel] ?? {};
      draft[stepLabel][questionLabel] = draft[stepLabel][questionLabel] ?? {};
      draft[stepLabel][questionLabel][dropdownType] = value;
    });

    if (
      this.schema.steps[this.currentAttentionStep].step_questions.length ===
        Object.keys(this.attentionData?.[stepLabel]).length &&
      !this.completedAttentionSteps.includes(this.currentAttentionStep)
    ) {
      this.completedAttentionSteps = [
        ...this.completedAttentionSteps,
        this.currentAttentionStep,
      ];
    }
    if (this.completedAttentionSteps.length === this.schema?.steps.length)
      this._calculateScores();
    this._dispatchModuleChangedEvent();
  };

  /**
   * @description produces {@link scores} of output and associated values.
   * Calculates {@link weightTotal} for attention calculator by iterating through each step and their questions in {@link #attentionData}
   */
  _calculateScores = () => {
    this.scores = { High: 0, Mid: 0, Low: 0, MidHigh: 0, MidLow: 0 };
    this.weightTotal = Object.values(this.attentionData)
      .flatMap(stepKey => Object.values(stepKey))
      .reduce((a, b) => a + (b.weight ?? 1), 0);
    // grab question keys from attentionData and determine associated step by matching keys to stepLabel key in schema
    Object.keys(this.attentionData).forEach(stepKey => {
      const questionKeys = Object.keys(this.attentionData[stepKey]);
      const step = this.schema.steps.find(
        schemaStep => schemaStep.step_label === stepKey,
      );
      // check to get rid of old data when questions are updated in schema
      questionKeys.forEach(qkey => {
        /**
         * Check to determine if {@link #attentionData} contains old data
         * Data becomes stale when {@link #schema} has been updated with new questions, invalidating old questions and associated stored answers
         */
        const questionMatch = step.step_questions.find(
          stepQuestion => stepQuestion.question_label === qkey,
        );
        // for each question determine the questions weight and output by matching the question to the associated question in the schema
        const questionWeight =
          this.attentionData[stepKey][qkey].weight ??
          questionMatch?.question_weight;
        const output =
          questionMatch?.question_options?.[
            this.attentionData[stepKey][qkey]?.value
          ]?.output;

        if (questionWeight === undefined || output === undefined) {
          // If there's an issue matching up saved data for a question with the module definition, delete & ignore it
          this.attentionData = produce(this.attentionData, draft => {
            delete draft[stepKey][qkey];
          });
        } else {
          // create running total of scores for each associated output value
          this.scores[output] =
            (this.scores[output] || 0) + questionWeight / this.weightTotal;
        }
      });
    });
  };

  /**
   * @fires CampaignAttentionCalculator~omni-module-changed
   */
  _dispatchModuleChangedEvent({ detail } = {}) {
    if (this.isInitializing) return;
    const { uuid_module: moduleId } = this.context;
    if (detail) this.recommendationCount = detail;
    /**
     * @event CampaignAttentionCalculator~omni-module-changed
     * @type {object}
     * @property {string} moduleId - This module's ID
     * @property {function} saveAction - A reference to {@link submit} bound to this
     * @example
     *   this.dispatchNewEvent('omni-module-changed', {
     *     detail: {
     *       moduleId,
     *       saveAction: this.submit.bind(this),
     *     },
     *   });
     */
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this._submit.bind(this),
        moduleType: 'multi rec attention calculator module',
      },
    });
  }

  /**
   * Submit method passed up to {@link CampaignStepDetail} via {@link _dispatchModuleChangedEvent}
   */
  async _submit() {
    await this.save({
      attentionData: this.attentionData,
      completedAttentionSteps: this.completedAttentionSteps,
      recommendationCount: this.recommendationCount,
    });
  }

  /**
   * @description renders all individual wizardStep elements based on number of steps in {@link #schema}
   */
  _renderWizard = () =>
    this.schema?.steps?.length
      ? html`<div class="wizard-container">
          ${this.schema.steps.map((step, stepNumber) =>
            this._renderWizardStep(step, stepNumber),
          )}
        </div>`
      : nothing;

  /**
   * @description renders individual wizardStep element
   * @param {number} stepNumber - index of step number {@link currentAttentionStep}
   * @param {Object} step - object that contains all associated step information
   */
  _renderWizardStep = (step, stepNumber) => html`
    <div class="wizard-step">
      <button
        class=${classMap({
          'wizard-button-active': this.currentAttentionStep === stepNumber,
          'wizard-button-complete':
            this.completedAttentionSteps.includes(stepNumber),
          'wizard-button': true,
        })}
        @click="${e => {
          e.preventDefault();
          e.stopPropagation();
          this.currentAttentionStep = stepNumber;
        }}"
      >
        ${this.completedAttentionSteps.includes(stepNumber)
          ? html` <omni-icon
              style="fill:rgb(var(--rgb-core-light));"
              class="is-size-1"
              icon-id="omni:informative:check"
            ></omni-icon>`
          : stepNumber + 1}
      </button>
      <div
        class="wizard-step-title is-size-6"
        style=${styleMap({
          color:
            this.currentAttentionStep === stepNumber ||
            this.completedAttentionSteps.includes(stepNumber)
              ? 'var(--primary)'
              : '',
        })}
      >
        ${step?.step_label}
      </div>
    </div>
    ${stepNumber === (this.schema?.steps?.length || 0) - 1
      ? nothing
      : html`
          <div
            class="${classMap({
              'wizard-line': true,
              'wizard-line-complete':
                this.completedAttentionSteps.includes(stepNumber),
            })}"
          ></div>
        `}
  `;

  /**
   * @returns {html} Rendered HTML for the component
   */
  render() {
    return html` <omni-style>
      <omni-tile class="module-wrapper">
        <div class="title-wrapper">
          <h1 class="title mb-0 is-2">
            ${this.schema?.module_title ?? 'Attention Requirement Calculator'}
          </h1>
          <label class="subtitle is-size-6 mb-6 ml-0">
            ${this.schema?.module_subtitle}
          </label>
        </div>
        ${this._renderWizard()}
        <campaign-attn-calc-multi-rec-dropdowns
          .schema=${this.schema}
          .context=${this.context}
          @dropdown-change=${this._handleChange}
          .attentionData=${this.attentionData}
          .currentAttentionStep=${this.currentAttentionStep}
        ></campaign-attn-calc-multi-rec-dropdowns>
        <campaign-attn-calc-multi-rec-score
          .recommendationCount=${this.recommendationCount}
          .schema=${this.schema}
          .attentionData=${this.attentionData}
          .completedAttentionSteps=${this.completedAttentionSteps}
          .scores=${this.scores}
          .context=${this.context}
        ></campaign-attn-calc-multi-rec-score>
      </omni-tile>
    </omni-style>`;
  }
}

OmniElement.register('campaign-attn-calc-multi-rec', CampaignAttnCalcMultiRec);
